import { Check, Clock, Tag, BadgeCheck } from "lucide-react";
import { TIERS, ONE_OFFS, COMPARISON, PRICING_NOTES, SITE } from "@/content/copy";
import { trackEvent } from "@/lib/analytics";

const tierNames = TIERS.items.map((t) => t.name);

function TierCard({ tier }) {
  return (
    <article
      data-testid={`tier-card-${tier.name.toLowerCase()}`}
      className={`relative flex flex-col rounded-2xl bg-white p-7 border transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${
        tier.popular ? "border-terracotta ring-1 ring-terracotta/30" : "border-border"
      }`}
    >
      {tier.popular && (
        <span className="absolute -top-3 left-6 rounded-full bg-terracotta px-3 py-1 text-xs font-bold text-white uppercase tracking-wide">
          Most popular
        </span>
      )}
      <h3 className="text-xl font-bold text-navy">{tier.name}</h3>
      <p className="mt-2 text-sm text-navy/70 leading-relaxed min-h-[40px]">{tier.promise}</p>
      <p className="mt-4">
        <span className="text-2xl font-extrabold text-navy">{tier.price}</span>
        <span className="ml-1 text-xs font-semibold text-navy/50">+ GST</span>
      </p>
      <ul className="mt-5 space-y-2.5 flex-1">
        {tier.includes.map((item) => (
          <li key={item} className="flex gap-2.5 text-sm text-navy/70 leading-relaxed">
            <Check className="w-4 h-4 mt-0.5 shrink-0 text-terracotta" aria-hidden="true" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
      <p className="mt-5 pt-4 border-t border-border text-xs font-semibold text-navy/50">
        Best for: {tier.bestFor}
      </p>
      <a
        href="#book"
        data-testid={`tier-cta-${tier.name.toLowerCase()}`}
        onClick={() => trackEvent("book_consultation_click", { location: `tier:${tier.name}` })}
        className={`mt-5 inline-flex justify-center items-center rounded-full px-6 py-3 text-sm font-bold transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none ${
          tier.popular
            ? "bg-terracotta text-white hover:bg-terracotta-hover focus-visible:ring-terracotta"
            : "border-2 border-navy text-navy hover:bg-navy hover:text-white focus-visible:ring-navy"
        }`}
      >
        {SITE.consultLabel}
      </a>
    </article>
  );
}

export default function Packages() {
  return (
    <section id="services" data-testid="services-section" className="py-20 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-sm font-bold uppercase tracking-widest text-terracotta">{TIERS.overline}</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-navy">{TIERS.title}</h2>
          <p className="mt-4 text-base text-navy/70 leading-relaxed">{TIERS.subtitle}</p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 stagger">
          {TIERS.items.map((tier) => (
            <TierCard key={tier.name} tier={tier} />
          ))}
        </div>

        <div className="mt-16 overflow-x-auto rounded-2xl border border-border bg-white" data-testid="comparison-table">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="bg-navy text-white">
                <th className="text-left px-5 py-4 font-bold">What's included</th>
                {tierNames.map((name) => (
                  <th key={name} className="px-4 py-4 font-bold text-center">
                    {name}
                    {name === "Growth" && (
                      <span className="ml-2 rounded-full bg-terracotta px-2 py-0.5 text-[10px] uppercase tracking-wide">Popular</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map((row, i) => (
                <tr key={row.feature} className={i % 2 ? "bg-background" : "bg-white"}>
                  <td className="px-5 py-3 font-semibold text-navy">{row.feature}</td>
                  {row.values.map((v, j) => (
                    <td key={j} className={`px-4 py-3 text-center ${v === "✓" ? "text-terracotta font-bold" : "text-navy/60"}`}>
                      {v}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-24">
          <p className="text-sm font-bold uppercase tracking-widest text-terracotta">{ONE_OFFS.overline}</p>
          <h3 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-navy">{ONE_OFFS.title}</h3>
          <p className="mt-3 text-base text-navy/70 leading-relaxed max-w-2xl">{ONE_OFFS.subtitle}</p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
            {ONE_OFFS.items.map((item) => (
              <article
                key={item.name}
                data-testid={`oneoff-card-${item.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="flex flex-col rounded-2xl bg-white p-7 border border-border transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                <h4 className="text-lg font-bold text-navy">{item.name}</h4>
                {item.badge && (
                  <span className="mt-2 inline-flex w-fit items-center rounded-full bg-terracotta-light px-3 py-1 text-xs font-bold text-terracotta-hover">
                    {item.badge}
                  </span>
                )}
                <p className="mt-3 text-sm text-navy/70 leading-relaxed flex-1">{item.text}</p>
                <div className="mt-5 pt-4 border-t border-border space-y-1.5 text-sm">
                  <p className="flex items-center gap-2 font-semibold text-navy">
                    <Tag className="w-3.5 h-3.5 text-terracotta" aria-hidden="true" /> {item.price}
                  </p>
                  <p className="flex items-center gap-2 text-navy/60">
                    <Clock className="w-3.5 h-3.5 text-terracotta" aria-hidden="true" /> {item.timeframe}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>

        <div className="mt-16 flex flex-wrap gap-3" data-testid="pricing-notes">
          {PRICING_NOTES.map((note) => (
            <span key={note} className="inline-flex items-center gap-2 rounded-full bg-white border border-border px-4 py-2 text-xs font-bold text-navy/70">
              <BadgeCheck className="w-3.5 h-3.5 text-terracotta" aria-hidden="true" /> {note}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
